import type { JsonObject } from "@/api";
import { getAtPath } from "./recipeCoerce";
import { BATCH_SIZE_OVERRIDE_KEY, LR_OVERRIDE_KEY } from "./recipeSeed";

// LR 자동 계산(선형 스케일). config 의 lr·batch_size 를 기준으로
// (총 GPU 수 × 유효 batch) / config batch 배율을 적용한다. training-infra Submit 과 같은 공식이다.

export type AutoLrResult = {
  // override 값으로 넣을 lr 표시 문자열(지수표기 없음).
  lr: string;
  factor: number;
  baseLr: number;
  baseBatch: number;
  globalBatch: number;
};

// config 값이 없거나 0 이하이면 계산할 수 없으므로 null 을 반환한다. batchSizeOverride 는 override 행의
// train.batch_size 값이다(비었으면 config 기본값을 쓴다).
export function computeAutoLr(
  data: JsonObject,
  totalGpus: number,
  batchSizeOverride: string | null,
): AutoLrResult | null {
  const baseLr = asPositiveNumber(getAtPath(data, LR_OVERRIDE_KEY));
  const baseBatch = asPositiveNumber(getAtPath(data, BATCH_SIZE_OVERRIDE_KEY));
  if (baseLr === null || baseBatch === null) {
    return null;
  }
  const gpus = totalGpus > 0 ? totalGpus : 1;
  const overridden =
    batchSizeOverride === null || batchSizeOverride.trim() === ""
      ? null
      : asPositiveNumber(Number(batchSizeOverride));
  const effectiveBatch = overridden ?? baseBatch;
  const globalBatch = gpus * effectiveBatch;
  const factor = globalBatch / baseBatch;
  return { lr: formatLr(baseLr * factor), factor, baseLr, baseBatch, globalBatch };
}

// 지수표기 대신 평문 소수 문자열(유효숫자 4자리)을 쓴다. 사용자가 값을 읽고 고치기 쉽다.
export function formatLr(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }
  const significant = Number(value.toPrecision(4));
  const plain = significant.toString();
  if (!plain.includes("e")) {
    return plain;
  }
  return significant.toFixed(20).replace(/0+$/, "").replace(/\.$/, "");
}

function asPositiveNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
}
